/**
 * HeaderCrowdContext — the wire between the header's nav strip and its account
 * chip (Asana 1217649894859355 — dynamic formatting in the top menu).
 *
 * The chip is the header's last reserve of width. When the Fleet Editor's facet
 * tabs no longer fit the space the row has left, the chip collapses from
 * `email + role badge` to an avatar and hands 205-225px back to the strip
 * (measured against the built CSS for `operator@example.com`, the low end with an
 * Operator badge and the high end with a Fleet Owner one; a longer address frees
 * more still). The two live in different places, so a channel is needed:
 *
 * - `OverflowNav` is the only thing that knows the tabs' natural width and the
 *   space they were given, so it decides (via `isCrowded`) and publishes
 *   `crowded`.
 * - `AppShell` renders the chip, and is the only thing that can measure it, so
 *   it publishes `reserve` — the width the collapse would free.
 *
 * The state lives in `AppShell`'s `useState`, not here: `AppShell` renders the
 * provider and so cannot consume it, and it needs `crowded` to pick which chip
 * form to render. This context only transports the pair.
 *
 * **The provider must wrap everything `AppShell` returns, not `{children}`.**
 * `OverflowNav` is created by `FleetEditor` but *rendered* by `AppShell` inside
 * `<header>`, while `children` renders in `<main>`. React context resolves by
 * render position, so a provider around `{children}` would be invisible to the
 * component that publishes: `isCrowded` would read the inert default below, the
 * chip would never collapse, and nothing else would fail.
 *
 * `reserve` is rounded by its publisher before it is stored. Widths come from
 * `getBoundingClientRect` and are fractional, so without that a
 * measure → publish → re-render → re-measure cycle loops until React throws
 * "Maximum update depth exceeded". `setCrowded` carries a boolean and needs no
 * such treatment — `useState` already bails on an unchanged one.
 */
import { createContext, useContext, type ReactNode } from 'react'

export interface HeaderCrowdValue {
  /** True while the chip should render its compact (avatar) form. */
  crowded: boolean
  /** Published by whatever measures the strip. */
  setCrowded: (v: boolean) => void
  /**
   * `chipExpandedWidth − chipCompactWidth`: the width collapsing the chip frees,
   * measured and rounded by the chip's owner. `undefined` until the chip's
   * mirrors have been measured, which `isCrowded` reads as "not known yet, stay
   * expanded".
   *
   * Read-only over this channel on purpose: the chip's owner is also the
   * component that renders the provider, so it publishes into its own state
   * directly and no consumer has any business setting this.
   */
  reserve: number | undefined
}

const HeaderCrowdContext = createContext<HeaderCrowdValue>({
  // Inert default, so a component mounted outside the shell — a facet rendered
  // directly in a test, `OverflowNav` on its own — neither crashes nor collapses
  // a chip that isn't there.
  crowded: false,
  setCrowded: () => {},
  reserve: undefined,
})

/**
 * Carries an externally owned value, because the owner (`AppShell`) is also the
 * component rendering this provider. `HeaderSlotProvider` owns its own state
 * instead; the difference is that the shell needs to read `crowded` itself.
 */
export function HeaderCrowdProvider(
  { value, children }: { value: HeaderCrowdValue; children: ReactNode },
) {
  return <HeaderCrowdContext.Provider value={value}>{children}</HeaderCrowdContext.Provider>
}

/** Read the crowding channel — to publish into it, or to render against it. */
export function useHeaderCrowd(): HeaderCrowdValue {
  return useContext(HeaderCrowdContext)
}
